import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import Messages from 'components/page-components/agents/Messages';
import { Box, Stack, Textarea, useToast } from '@chakra-ui/react';
import { TypingDots } from 'components/custom-components/icons/TypingDot';
import { SERVER_WS_URL } from 'utils/constants';
import ZaraButton from 'components/custom-components/buttons/Button';
import AgentResponseModal from 'components/modals/AgentResponseModal';
import { useError } from 'error-provider/ErrorProvider';
import useServerApi from 'hooks/useServerApi';

interface SimulateChatProps {
    agentId: number;
    pathname: string;
}

const SimulateChat: React.FC<SimulateChatProps> = ({ agentId, pathname }) => {
    const endOfChat = useRef<HTMLDivElement>(null);
    const toast = useToast();
    const { showError } = useError();
    const [{ post }] = useServerApi();
    const [isFixing, setIsFixing] = useState(false);
    const [fixText, setFixText] = useState('');
    const [isOpenPopover, setIsOpenPopover] = useState(false);
    const [subscriptionData, setSubscriptionData] = useState<{ answer: string }[]>([]);
    const [socket, setSocket] = useState<WebSocket | null>(null);
    const [isAgentTyping, setIsAgentTyping] = useState(false);
    const [userMessage, setUserMessage] = useState({ answer: '', isSendingFix: false });
    const [fixAttempts, setFixAttempts] = useState<number>(0);

    const isTestChat = pathname === '/test-chat'; // PATHS.TEST_CHAT is '/test-chat'

    const handleSendMessage = () => {
        socket?.send(userMessage?.answer);
        // @ts-ignore
        setSubscriptionData((prev) => [...prev, userMessage]);
        setUserMessage((prev) => ({ ...prev, answer: '' }));
    };

    const onEnterPress = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (e.keyCode === 13 && e.shiftKey === false && userMessage?.answer?.trim().length) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const onChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        setUserMessage((prev) => ({ ...prev, answer: e.target.value }));
    };

    const onCloseModal = () => {
        setIsOpenPopover(false);
    };

    const onChatFix = async () => {
        try {
            setIsFixing(true);
            const [success, res] = await post('/rest_endpoints/fix-agent-response', {
                agentId,
                fixText,
                userMessage: userMessage.answer,
                subscriptionData: subscriptionData.map((item) => ({ answer: item.answer })),
            });
            if (success) {
                if (res.data?.error) {
                    toast({
                        title: res?.data?.error || 'Failed to fix agent response',
                        status: 'error',
                        isClosable: true,
                    });
                    return;
                }
                setFixAttempts((prev: number) => prev + 1);
                toast({
                    title: 'Agent response fixed successfully',
                    status: 'success',
                    isClosable: true,
                });
            } else {
                toast({
                    title: res?.message || 'Failed to fix agent response',
                    status: 'error',
                    isClosable: true,
                });
            }
        } catch (error) {
            showError('Failed to fix agent response', error as Error);
        } finally {
            setUserMessage((prev) => ({ ...prev, isSendingFix: false }));
            setSocket(null);
            setSubscriptionData([]);
            setIsOpenPopover(false);
            setFixText('');
            setIsFixing(false);
        }
    };

    const handleUndoChanges = async () => {
        if (!window.confirm('Are you sure you want to undo changes to the training guide?')) return;
        try {
            const [success] = await post('/rest_endpoints/revert-agent-changes', { agentId });
            if (success) {
                setFixAttempts(0);
                toast({
                    title: 'Changes to the training guide are reverted',
                    status: 'success',
                    isClosable: true,
                });
            } else {
                toast({
                    title: 'Failed to revert changes',
                    status: 'error',
                    isClosable: true,
                });
            }
        } catch (error) {
            showError('Failed to revert changes', error as Error);
        }
    }

    useEffect(() => {
        endOfChat?.current?.scrollIntoView({
            behavior: 'auto',
            block: 'center',
        });
    }, [subscriptionData, isAgentTyping]);

    useEffect(() => {
        if (socket) return;
        if (agentId) {
            const newSocket = new WebSocket(`${SERVER_WS_URL}/ws-agent-chat/${agentId}/`);
            newSocket.addEventListener('message', (event) => {
                // @ts-ignore
                setSubscriptionData((prev) => [...prev, JSON.parse(event.data)]);
            });
            setSocket(newSocket);
        }
        return () => {
            // @ts-ignore
            socket?.close();
        };
    }, [agentId, socket]);

    useEffect(() => {
        if (!subscriptionData.length) return;
        // @ts-ignore
        if (subscriptionData?.slice(-1)[0]?.audio_preview !== null) {
            setIsAgentTyping(true);
        } else {
            setIsAgentTyping(false);
        }
    }, [subscriptionData]);

    return (
        <Stack>
            <AgentResponseModal
                fixText={fixText}
                isOpenPopover={isOpenPopover}
                onClose={onCloseModal}
                setFixText={setFixText}
                onHandleFix={onChatFix}
                isLoading={isFixing}
            />
            <Box minH={300} maxH={400} overflowY="auto">
                <Messages
                    isTestChat={isTestChat}
                    setIsOpenPopover={setIsOpenPopover}
                    subscriptionData={subscriptionData}
                />
                {isAgentTyping && <TypingDots />}
                <Box ref={endOfChat}></Box>
            </Box>
            {fixAttempts > 0 && (
                <Box mt={2}>
                    <button
                        type="button"
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                            e.preventDefault();
                            handleUndoChanges();
                        }}
                        style={{ 
                            background: 'none', 
                            border: 'none', 
                            padding: 0, 
                            color: '#3182ce', 
                            textDecoration: 'underline', 
                            fontWeight: 500,
                            cursor: 'pointer'
                        }}
                    >
                        Undo changes
                    </button>
                </Box>
            )}
            <Stack direction='row'>
                <Textarea
                    // @ts-ignore
                    onKeyDown={onEnterPress}
                    placeholder='Type your message...'
                    value={userMessage.answer}
                    onChange={onChange}
                    minH='40px'
                />
                <ZaraButton variant='primary' w='auto' fontSize='sm' onClick={handleSendMessage}>
                    Send
                </ZaraButton>
            </Stack>
        </Stack>
    );
};

export default SimulateChat;